import type { QueryClient } from "@tanstack/react-query";
import {
  EventIdPublic,
  EventParticipantIdPublic,
} from "@shared/schemas/branded-id.schemas";
import {
  useQuery,
  useMutation,
  useInfiniteQuery,
  keepPreviousData,
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { applicationLogger } from "@frontend/shared/infrastructure/logger/application-logger.singleton";
import { BaseApiHooksRepository } from "../../base-api-hooks.repository";
import type {
  GetDirectoryEntryOptions,
  GetSuggestionsOptions,
  GetEntriesOptions,
  GetDirectoryEntriesBatchOptions,
  GetConnectionRequestsOptions,
  GetConnectedProfilesOptions,
  GetProfileDetailOptions,
  GetContactInfoOptions,
  PrefetchAllPagesOptions,
  PrefetchAllPagesResult,
  RequestIntroVariables,
  RespondToRequestVariables,
  MarkIntroSeenVariables,
  CreateExportVariables,
  RespondToRequestContext,
} from "./directory-repository-options.types";
import {
  createDirectoryApi,
  type GetProfileDetailApiResponse,
} from "./directory.api";
import type { AxiosInstance } from "axios";
import { DIRECTORY_QUERY_KEYS } from "./directory-query-keys.constants";
import type { DirectoryCard } from "@frontend/shared/domain/types/directory-card.types";
import { DirectoryEntrySchema } from "@shared/schemas/directory-entry.schemas";
import type { ProfileContactInfo } from "@shared/schemas/profile-detail.schemas";
import type { DirectoryProfile } from "@frontend/shared/domain/types/directory-profile.types";
import {
  DirectoryConnectionStatus,
  IntroContactInfoSchema,
} from "@shared/schemas/profile-detail.schemas";
import { IntroRequestStatus } from "@shared/schemas/intro-request-state.schemas";
import { SuggestionsPageSchema } from "@shared/schemas/suggestion.schemas";
import { ConnectionRequestPartyViewSchema } from "@shared/schemas/connection-request-views.schemas";
import {
  ConnectionRequestSummarySchema,
  type ConnectionRequestSummary,
} from "@shared/schemas/connection-request.schemas";
import {
  buildDirectoryProfile,
  connectionRequestSummaryToDirectoryCard,
  directoryEntryToCard,
  resolveConnectionRequestSummaryPhotoUrls,
} from "./directory-card-builder";
import type { Paginated } from "@shared/schemas/paginated.schemas";
import type { SuggestionsPage } from "@shared/schemas/suggestion.schemas";

const DEFAULT_ENTRIES_PAGE_SIZE: number =
  import.meta.env["VITE_TEST_ENTRIES_PAGE_SIZE"] !== undefined
    ? Number(import.meta.env["VITE_TEST_ENTRIES_PAGE_SIZE"])
    : 100;

export class DirectoryHooksHttpRepositoryImpl extends BaseApiHooksRepository {
  private readonly api;

  constructor(queryClient: QueryClient, apiClient: AxiosInstance) {
    super(queryClient);
    this.api = createDirectoryApi(apiClient);
  }

  createHooks() {
    return {
      useGetEntries: (options: GetEntriesOptions) =>
        useInfiniteQuery({
          queryKey: DIRECTORY_QUERY_KEYS.entries(
            options.eventIdPublic,
            options.searchQuery,
            options.searchMode,
          ),
          queryFn: async (context): Promise<Paginated<DirectoryCard>> => {
            // React Query aborts a superseded fetch only once its signal is read.
            // Browse pages are left alone: the prefetch loop reads them all.
            const signal =
              options.searchQuery === undefined ? undefined : context.signal;
            const response = await this.api.getEntries(
              {
                eventIdPublic: options.eventIdPublic,
                page: context.pageParam,
                limit: options.limit ?? DEFAULT_ENTRIES_PAGE_SIZE,
                sortBy: "displayName",
                sortOrder: "asc",
                ...(options.searchQuery !== undefined && {
                  searchQuery: options.searchQuery,
                }),
                ...(options.searchMode !== undefined && {
                  searchMode: options.searchMode,
                }),
              },
              signal,
            );
            return {
              ...response,
              items: response.items.map((dto) =>
                directoryEntryToCard(DirectoryEntrySchema.parse(dto)),
              ),
            };
          },
          initialPageParam: 1,
          getNextPageParam: (lastPage) =>
            lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
          enabled: options.enabled ?? true,
          // Keep previous data visible while new query loads (prevents focus loss on search)
          placeholderData: keepPreviousData,
        }),

      useGetConnectionRequests: (options: GetConnectionRequestsOptions) =>
        this.query<readonly unknown[], readonly ConnectionRequestSummary[]>({
          key: DIRECTORY_QUERY_KEYS.connectionRequests(
            options.eventIdPublic,
            options.direction ?? "received",
          ),
          fn: () =>
            this.api.getConnectionRequests({
              eventIdPublic: options.eventIdPublic,
              sortBy: "displayName",
              sortOrder: "asc",
              ...(options.direction !== undefined && {
                direction: options.direction,
              }),
            }),
          parse: (dtos) =>
            dtos.map((dto) =>
              resolveConnectionRequestSummaryPhotoUrls(
                ConnectionRequestSummarySchema.parse(dto),
                EventIdPublic.unwrap(options.eventIdPublic),
              ),
            ),
          enabled: options.enabled ?? true,
        }),

      useGetConnectedProfiles: (options: GetConnectedProfilesOptions) =>
        this.query<
          {
            readonly received: readonly unknown[];
            readonly sent: readonly unknown[];
          },
          readonly DirectoryCard[]
        >({
          key: DIRECTORY_QUERY_KEYS.connected(options.eventIdPublic),
          fn: () =>
            this.api.getConnectedProfiles({
              ...options,
              sortBy: "displayName",
              sortOrder: "asc",
            }),
          parse: (raw) => {
            const parseItem = (dto: unknown): ConnectionRequestSummary =>
              resolveConnectionRequestSummaryPhotoUrls(
                ConnectionRequestSummarySchema.parse(dto),
                EventIdPublic.unwrap(options.eventIdPublic),
              );
            const received = raw.received.map(parseItem);
            const sent = raw.sent.map(parseItem);
            // Backend returns active + accepted intros under
            // `includeAccepted=true`; filter to ACCEPTED only.
            const acceptedReceived = received.filter(
              (item) => item.typeData.status === IntroRequestStatus.ACCEPTED,
            );
            const acceptedSent = sent.filter(
              (item) => item.typeData.status === IntroRequestStatus.ACCEPTED,
            );
            const fromProfiles = acceptedReceived.map((summary) =>
              connectionRequestSummaryToDirectoryCard(
                summary,
                "from",
                options.eventIdPublic,
              ),
            );
            const toProfiles = acceptedSent.map((summary) =>
              connectionRequestSummaryToDirectoryCard(
                summary,
                "to",
                options.eventIdPublic,
              ),
            );
            return [
              ...new Map(
                [...fromProfiles, ...toProfiles].map((p) => [
                  p.participantIdPublic,
                  p,
                ]),
              ).values(),
            ];
          },
          enabled: options.enabled ?? true,
        }),

      useGetDirectoryEntry: (options: GetDirectoryEntryOptions) =>
        this.query<unknown, DirectoryCard | null>({
          key: DIRECTORY_QUERY_KEYS.entry(
            options.eventIdPublic,
            options.participantIdPublic,
          ),
          fn: () => this.api.getDirectoryEntry(options),
          parse: (dto) =>
            dto === null
              ? null
              : directoryEntryToCard(DirectoryEntrySchema.parse(dto)),
          enabled: options.enabled ?? true,
          refetchOnMount: "always",
        }),

      useGetProfileDetail: (options: GetProfileDetailOptions) =>
        this.query<GetProfileDetailApiResponse, DirectoryProfile>({
          key: DIRECTORY_QUERY_KEYS.profileDetail(
            options.eventIdPublic,
            options.participantIdPublic,
          ),
          fn: () => this.api.getProfileDetail(options),
          parse: (raw) =>
            buildDirectoryProfile(
              {
                participantDto:
                  raw.participantDto === undefined
                    ? undefined
                    : DirectoryEntrySchema.parse(raw.participantDto),
                introsItems: raw.introsItems.map((dto) =>
                  ConnectionRequestSummarySchema.parse(dto),
                ),
              },
              options.participantIdPublic,
            ),
          enabled: options.enabled ?? true,

          // This prevents stale data when navigating from directory lists (BOC-565)
          refetchOnMount: "always",
        }),

      useGetSuggestions: (options: GetSuggestionsOptions) =>
        this.query<unknown, SuggestionsPage>({
          key: DIRECTORY_QUERY_KEYS.suggestions(options.eventIdPublic),
          fn: () =>
            this.api.getSuggestions({
              eventIdPublic: options.eventIdPublic,
              participantIdPublic: options.participantIdPublic,
            }),
          parse: (dto) => SuggestionsPageSchema.parse(dto),
          enabled: options.enabled ?? true,

          // Nothing pushes the deck: a run that finishes while the tab is open
          // is only noticed on the next mount, so re-ask rather than serve a
          // cached "recomputing" past the run that set it.
          refetchOnMount: "always",
        }),

      useGetDirectoryEntriesBatch: (
        options: GetDirectoryEntriesBatchOptions,
      ) => {
        const normalizedIdsKey = [...options.participantIdsPublic]
          .map((id) => EventParticipantIdPublic.unwrap(id))
          .sort()
          .join(",");
        const normalizedIds = useMemo(
          () =>
            Array.from(new Set(options.participantIdsPublic)).sort((a, b) =>
              EventParticipantIdPublic.unwrap(a).localeCompare(
                EventParticipantIdPublic.unwrap(b),
              ),
            ),
          // eslint-disable-next-line react-hooks/exhaustive-deps -- content-keyed
          [normalizedIdsKey],
        );
        return useQuery({
          queryKey: DIRECTORY_QUERY_KEYS.entriesBatch(
            options.eventIdPublic,
            normalizedIds,
          ),
          queryFn: async (): Promise<readonly DirectoryCard[]> => {
            const response = await this.api.getDirectoryEntriesBatch({
              eventIdPublic: options.eventIdPublic,
              participantIdsPublic: normalizedIds,
            });
            if (response.items.length < normalizedIds.length) {
              applicationLogger.warn(
                "Batch directory-entries returned fewer items than requested",
                {
                  requested: normalizedIds.length,
                  returned: response.items.length,
                  eventIdPublic: options.eventIdPublic,
                },
              );
            }
            return response.items.map((dto) =>
              directoryEntryToCard(DirectoryEntrySchema.parse(dto)),
            );
          },
          enabled: (options.enabled ?? true) && normalizedIds.length > 0,
        });
      },

      useGetContactInfo: (options: GetContactInfoOptions) =>
        useQuery({
          queryKey: DIRECTORY_QUERY_KEYS.contactInfo(
            options.eventIdPublic,
            options.requestIdPublic,
          ),
          queryFn: async (): Promise<ProfileContactInfo> => {
            const dto = await this.api.getContactInfo({
              eventIdPublic: options.eventIdPublic,
              requestIdPublic: options.requestIdPublic,
            });
            return IntroContactInfoSchema.parse(dto).contactInfo;
          },
          enabled: options.enabled ?? true,
          // Don't retry on 403 (forbidden) - intro not accepted or actor not a party
          retry: (failureCount, error) => {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- AxiosError has response property
            const axiosError = error as { response?: { status?: number } };
            if (axiosError.response?.status === 403) {
              return false;
            }
            return failureCount < 3;
          },
        }),

      useRequestIntro: () =>
        useMutation({
          mutationFn: async (variables: RequestIntroVariables) => {
            const dto = await this.api.requestIntro({
              eventIdPublic: variables.eventIdPublic,
              targetParticipantIdPublic: variables.targetParticipantIdPublic,
              ...(variables.message !== undefined && {
                message: variables.message,
              }),
              origin: variables.origin,
              originData: variables.originData,
            });
            return resolveConnectionRequestSummaryPhotoUrls(
              ConnectionRequestSummarySchema.parse(dto),
              EventIdPublic.unwrap(variables.eventIdPublic),
            );
          },

          onError: (error) => {
            applicationLogger.error(
              "Failed to request intro",
              "DirectoryHooksHttpRepository.useRequestIntro",
              error instanceof Error ? error : new Error(String(error)),
            );
          },

          onSuccess: (data, variables) => {
            this.invalidateQueries(
              DIRECTORY_QUERY_KEYS.connectionRequestsBase(
                variables.eventIdPublic,
              ),
            );

            const profileKey = DIRECTORY_QUERY_KEYS.profileDetail(
              variables.eventIdPublic,
              variables.targetParticipantIdPublic,
            );
            const cachedProfile =
              this.getCachedData<DirectoryProfile>(profileKey);
            if (cachedProfile !== undefined) {
              this.setCachedData(profileKey, {
                ...cachedProfile,
                connectionStatus: DirectoryConnectionStatus.REQUESTED,
                pendingRequestIdPublic: data.idPublic,
              });
            }
          },
        }),

      useRespondToRequest: () =>
        useMutation({
          mutationFn: async (variables: RespondToRequestVariables) => {
            const dto = await this.api.respondToRequest(variables);
            return ConnectionRequestPartyViewSchema.parse(dto);
          },

          onMutate: async (variables): Promise<RespondToRequestContext> => {
            await this.queryClient.cancelQueries({
              queryKey: DIRECTORY_QUERY_KEYS.connectionRequests(
                variables.eventIdPublic,
                "received",
              ),
            });
            await this.queryClient.cancelQueries({
              queryKey: DIRECTORY_QUERY_KEYS.connected(variables.eventIdPublic),
            });

            const previousRequests = this.getCachedData<
              readonly ConnectionRequestSummary[]
            >(
              DIRECTORY_QUERY_KEYS.connectionRequests(
                variables.eventIdPublic,
                "received",
              ),
            );
            const previousConnected = this.getCachedData<
              readonly DirectoryCard[]
            >(DIRECTORY_QUERY_KEYS.connected(variables.eventIdPublic));

            return { previousRequests, previousConnected };
          },

          onError: (error, variables, context) => {
            applicationLogger.error(
              "Failed to respond to connection request",
              "DirectoryHooksHttpRepository.useRespondToRequest",
              error instanceof Error ? error : new Error(String(error)),
            );

            if (context?.previousRequests !== undefined) {
              this.setCachedData(
                DIRECTORY_QUERY_KEYS.connectionRequests(
                  variables.eventIdPublic,
                  "received",
                ),
                context.previousRequests,
              );
            }
            if (context?.previousConnected !== undefined) {
              this.setCachedData(
                DIRECTORY_QUERY_KEYS.connected(variables.eventIdPublic),
                context.previousConnected,
              );
            }
          },

          onSuccess: (data, variables) => {
            const queryKey = DIRECTORY_QUERY_KEYS.connectionRequests(
              variables.eventIdPublic,
              "received",
            );

            this.queryClient.setQueryData<readonly ConnectionRequestSummary[]>(
              queryKey,
              (oldRequests) => {
                if (oldRequests === undefined) {
                  return oldRequests;
                }

                return oldRequests.filter(
                  (request) => request.idPublic !== data.idPublic,
                );
              },
            );

            if (variables.action === "accept") {
              this.invalidateQueries(
                DIRECTORY_QUERY_KEYS.connected(variables.eventIdPublic),
              );
            }
          },
        }),

      useMarkIntroSeen: () =>
        useMutation({
          mutationFn: async (variables: MarkIntroSeenVariables) => {
            const dto = await this.api.markIntroSeen({
              requestIdPublic: variables.requestIdPublic,
              eventIdPublic: variables.eventIdPublic,
            });
            return ConnectionRequestPartyViewSchema.parse(dto);
          },
          onError: (error) => {
            // Log but don't throw - marking as seen is non-critical
            applicationLogger.error(
              "Failed to mark intro as seen",
              "DirectoryHooksHttpRepository.useMarkIntroSeen",
              error instanceof Error ? error : new Error(String(error)),
            );
          },
          onSuccess: (_data, variables) => {
            this.invalidateQueries(
              DIRECTORY_QUERY_KEYS.connectionRequests(
                variables.eventIdPublic,
                "received",
              ),
            );
          },
        }),

      // No onError handler: nothing has reached the member yet, so the caller
      // has to see a failure in order to tell it apart from the window being
      // closed.
      useCreateExport: () =>
        useMutation({
          mutationFn: async (variables: CreateExportVariables) =>
            this.api.createExport({
              eventIdPublic: variables.eventIdPublic,
            }),
        }),

      usePrefetchAllPages: (
        options: PrefetchAllPagesOptions,
      ): PrefetchAllPagesResult => {
        const { entriesQuery, enabled, delayMs = 2000, maxPages = 0 } = options;
        const [isPrefetching, setIsPrefetching] = useState(false);
        const hasStartedRef = useRef(false);

        const { isLoading, hasNextPage, fetchNextPage } = entriesQuery;

        const isComplete = enabled && !isLoading && hasNextPage === false;

        useEffect(() => {
          if (
            !enabled ||
            isLoading ||
            hasNextPage !== true ||
            hasStartedRef.current
          ) {
            return;
          }

          hasStartedRef.current = true;
          const abort = new AbortController();

          const run = async (): Promise<void> => {
            await new Promise((resolve) => {
              setTimeout(resolve, delayMs);
            });
            if (abort.signal.aborted) return;

            setIsPrefetching(true);
            let pagesFetched = 0;

            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- hasNextPage is updated by React Query after each fetchNextPage
            while (!abort.signal.aborted) {
              const result = await fetchNextPage();
              pagesFetched++;

              const reachedMaxPages = maxPages > 0 && pagesFetched >= maxPages;
              if (result.hasNextPage !== true || reachedMaxPages) break;
            }

            setIsPrefetching(false);
          };

          void run();

          return () => {
            abort.abort();
          };
        }, [enabled, isLoading, hasNextPage, fetchNextPage, delayMs, maxPages]);

        useEffect(() => {
          if (!enabled) {
            hasStartedRef.current = false;
          }
        }, [enabled]);

        return { isPrefetching, isComplete };
      },
    };
  }
}
